// Klub / Návyky (27.8.2026, zadání Ládi).
//
// Tři věci na jednom místě: katalog návyků, které si členka vybírá jako
// měsíční výzvu, návrhy poslané členkami a texty automatických zpráv.
// Zdrojem pravdy jsou tabulky klub_navyky_katalog a klub_zpravy_sablony;
// texty se odsud přepisují bez nasazení.
//
// PROČ SE NEMAŽE: návyk, který si členka rozběhla, je navázaný na její
// masterplan. Kdyby zmizel z katalogu, nešlo by dohledat, proč ho má.
// Vypnutý návyk se jen přestane nabízet.
//
// VZHLED podle stránky Klienti: jedna bílá karta, přepínač záložek,
// filtry s počty a pak tabulka. Na úzkém displeji se vedlejší sloupce
// schovají a zůstane jen návyk a ovládání, aby se nemuselo posouvat do stran.
import React, { useMemo, useState } from "react";
import {
  ListChecks, Plus, Pencil, Eye, EyeOff, Lightbulb, Check, X, Loader2, Search, Bell, RefreshCw, MessageSquare,
} from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Zalozky, RozbalovaciVyber, Modal, HlavickaOkna, ErrorNote, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "../../components/PrehledDlazdice.jsx";
import { toast } from "../../components/Toast.jsx";

const TYPY = [{ v: "denni", l: "Denní" }, { v: "tydenni", l: "Týdenní" }];
const NAROCNOST = [{ v: 1, l: "1 (snadné)" }, { v: 2, l: "2 (střední)" }, { v: 3, l: "3 (náročné)" }];

async function post(body) {
  const r = await apiFetch("/api/klub-navyky-sprava", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Akce selhala");
  return j;
}

const prazdny = (kategorie) => ({
  kategorie, emoji: "", text: "", proc: "", typ: "denni", narocnost: 2,
  resi: [], parametr: null, skupina: "", jenRucne: false, aktivni: true, poradi: 0,
});

// ── Okno s formulářem ─────────────────────────────────────────────────
// Na mobilu je formulář v okně použitelnější než rozbalený uvnitř řádku:
// nemíchá se s ostatními návyky a jde zavřít klepnutím vedle.
function Okno({ titul, ikona, onZavrit, children }) {
  return (
    <Modal
      open
      onClose={onZavrit}
      plnaHlavicka
      header={<HlavickaOkna ikona={ikona} nazev={titul} onClose={onZavrit} />}
    >
      {children}
    </Modal>
  );
}

function Popisek({ children, doplnek }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {children}{doplnek && <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">{doplnek}</span>}
    </label>
  );
}

function FormularNavyku({ navyk, kategorie, klice, onUlozit, onZrusit, ukladam }) {
  const [n, setN] = useState(() => ({ ...navyk, skupina: navyk.skupina || "" }));
  const zmen = (k, v) => setN((s) => ({ ...s, [k]: v }));
  const maParametr = Boolean(n.parametr);

  const prepniKlic = (k) => setN((s) => ({
    ...s, resi: s.resi.includes(k) ? s.resi.filter((x) => x !== k) : [...s.resi, k],
  }));
  const prepniParametr = () => setN((s) => ({
    ...s,
    parametr: s.parametr ? null
      : { nazev: "Kolikrát týdně", min: 1, max: 5, krok: 1, vychozi: 2, jednotka: "×" },
  }));
  const zmenParametr = (k, v) => setN((s) => ({ ...s, parametr: { ...s.parametr, [k]: v } }));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[88px_1fr]">
        <div>
          <Popisek>Emoji</Popisek>
          <input className="field" value={n.emoji} maxLength={8}
            onChange={(e) => zmen("emoji", e.target.value)} placeholder="🥗" />
        </div>
        <div>
          <Popisek doplnek="čte ho členka na kartě">Text návyku</Popisek>
          <input className="field" value={n.text} maxLength={120}
            onChange={(e) => zmen("text", e.target.value)} placeholder="Zelenina třikrát denně" />
        </div>
      </div>

      <div>
        <Popisek doplnek="věta pod textem, vysvětluje smysl">Proč</Popisek>
        <textarea className="field min-h-[80px] resize-y" value={n.proc} maxLength={400}
          onChange={(e) => zmen("proc", e.target.value)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Popisek>Kategorie</Popisek>
          <select className="field" value={n.kategorie} onChange={(e) => zmen("kategorie", e.target.value)}>
            {Object.entries(kategorie).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <Popisek>Typ</Popisek>
          <select className="field" value={n.typ} onChange={(e) => zmen("typ", e.target.value)}>
            {TYPY.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <div>
          <Popisek>Náročnost</Popisek>
          <select className="field" value={n.narocnost}
            onChange={(e) => zmen("narocnost", Number(e.target.value))}>
            {NAROCNOST.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={maParametr} onChange={prepniParametr} className="h-4 w-4" />
          Členka si nastaví číslo (kroky, počet za týden, hodiny)
        </label>
        {maParametr && (
          <>
            <p className="mt-1.5 text-xs text-slate-500">
              Do textu se dosadí přes <code className="rounded bg-white px-1">{"{hodnota}"}</code>.
              U týdenního návyku je z čísla cíl na týden.
            </p>
            <div className="mt-2.5 grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
              <input className="field" placeholder="Popisek" value={n.parametr.nazev}
                onChange={(e) => zmenParametr("nazev", e.target.value)} />
              <input className="field" type="number" placeholder="min" value={n.parametr.min}
                onChange={(e) => zmenParametr("min", Number(e.target.value))} />
              <input className="field" type="number" placeholder="max" value={n.parametr.max}
                onChange={(e) => zmenParametr("max", Number(e.target.value))} />
              <input className="field" type="number" placeholder="krok" value={n.parametr.krok}
                onChange={(e) => zmenParametr("krok", Number(e.target.value))} />
              <input className="field" type="number" placeholder="výchozí" value={n.parametr.vychozi}
                onChange={(e) => zmenParametr("vychozi", Number(e.target.value))} />
              <input className="field" placeholder="jednotka" value={n.parametr.jednotka}
                onChange={(e) => zmenParametr("jednotka", e.target.value)} />
            </div>
          </>
        )}
      </div>

      <div>
        <Popisek doplnek="podle toho ho navrhovač nabídne">Co návyk řeší</Popisek>
        <div className="flex flex-wrap gap-1.5">
          {klice.map((k) => {
            const je = n.resi.includes(k.klic);
            return (
              <button key={k.klic} type="button" onClick={() => prepniKlic(k.klic)} title={k.otazka}
                className={"rounded-full border px-3 py-1 text-xs font-medium transition " +
                  (je ? "border-plum-600 bg-plum-600 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-plum-300")}>
                {k.text}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Popisek doplnek="automat z ní nabídne nejvýš jeden">Skupina</Popisek>
          <input className="field" value={n.skupina} placeholder="např. mobilita"
            onChange={(e) => zmen("skupina", e.target.value)} />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={n.jenRucne} className="h-4 w-4"
              onChange={(e) => zmen("jenRucne", e.target.checked)} />
            Automat nenabízí, členka si ho najde sama
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <button type="button" disabled={ukladam} onClick={() => onUlozit(n)}
          className="btn btn-primary">
          {ukladam ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Uložit
        </button>
        <button type="button" onClick={onZrusit}
          className="btn btn-secondary">
          Zrušit
        </button>
      </div>
    </div>
  );
}

function FormularZpravy({ zprava, onUlozit, onZrusit, ukladam }) {
  const [z, setZ] = useState(() => ({ ...zprava }));
  const zmen = (k, v) => setZ((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        <strong className="font-semibold text-slate-700">Kdy se pošle:</strong> {z.kdy}
      </p>
      <div>
        <Popisek>Nadpis</Popisek>
        <input className="field" value={z.nadpis} maxLength={120}
          onChange={(e) => zmen("nadpis", e.target.value)} />
      </div>
      <div>
        <Popisek>Text</Popisek>
        <textarea className="field min-h-[110px] resize-y" value={z.text} maxLength={600}
          onChange={(e) => zmen("text", e.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Popisek doplnek="kam vede tlačítko">Odkaz</Popisek>
          <input className="field" value={z.odkaz} placeholder="/zvyky"
            onChange={(e) => zmen("odkaz", e.target.value)} />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={z.aktivni !== false} className="h-4 w-4"
              onChange={(e) => zmen("aktivni", e.target.checked)} />
            Zprávu posílat
          </label>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Do nadpisu i textu se dosadí: <code className="rounded bg-slate-100 px-1">{"{mesic}"}</code> (v září),{" "}
        <code className="rounded bg-slate-100 px-1">{"{dny}"}</code> (dní se zápisem),{" "}
        <code className="rounded bg-slate-100 px-1">{"{serie}"}</code> (délka série),{" "}
        <code className="rounded bg-slate-100 px-1">{"{hodnost}"}</code> (název hodnosti).
      </p>
      <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <button type="button" disabled={ukladam} onClick={() => onUlozit(z)}
          className="btn btn-primary">
          {ukladam ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Uložit
        </button>
        <button type="button" onClick={onZrusit}
          className="btn btn-secondary">
          Zrušit
        </button>
      </div>
    </div>
  );
}

// ── Stránka ───────────────────────────────────────────────────────────
export default function KlubNavyky() {
  // bezKese: katalog se tu přepisuje, takže nesmí chodit z localStorage,
  // jinak by po uložení chvíli svítila stará verze.
  const { data, loading, error, refetch } = useApiData("/api/klub-navyky-sprava", { bezKese: true });
  const [zalozka, setZalozka] = useState("katalog");
  const [filtr, setFiltr] = useState("vse");
  const [hledani, setHledani] = useState("");
  const [edit, setEdit] = useState(null);        // { navyk } nebo { zprava } nebo { navyk, napad }
  const [ukladam, setUkladam] = useState(false);

  const katalog = data?.katalog || [];
  const kategorie = data?.kategorie || {};
  const klice = data?.klice || [];
  const napady = data?.napady || [];
  const zpravy = data?.zpravy || [];

  const videt = useMemo(() => {
    const h = hledani.trim().toLowerCase();
    return katalog.filter((n) => (filtr === "vse" || n.kategorie === filtr)
      && (!h || n.text.toLowerCase().includes(h) || (n.proc || "").toLowerCase().includes(h)));
  }, [katalog, filtr, hledani]);

  async function ulozitNavyk(navyk, napadId) {
    setUkladam(true);
    try {
      await post(napadId ? { action: "napad-do-katalogu", id: napadId, navyk } : { action: "ulozit", navyk });
      toast("Návyk uložen.", "success");
      setEdit(null);
      refetch();
    } catch (e) { toast(e.message, "error"); }
    finally { setUkladam(false); }
  }

  async function ulozitZpravu(zprava) {
    setUkladam(true);
    try {
      await post({ action: "zprava-ulozit", zprava });
      toast("Zpráva uložena.", "success");
      setEdit(null);
      refetch();
    } catch (e) { toast(e.message, "error"); }
    finally { setUkladam(false); }
  }

  async function prepnout(n) {
    try {
      await post({ action: "prepnout", id: n.id, aktivni: !n.aktivni });
      toast(n.aktivni ? "Návyk se přestal nabízet." : "Návyk se zase nabízí.", "success");
      refetch();
    } catch (e) { toast(e.message, "error"); }
  }

  async function napadStav(id, stav) {
    try { await post({ action: "napad-stav", id, stav }); refetch(); }
    catch (e) { toast(e.message, "error"); }
  }

  const zalozky = [
    { id: "katalog", label: "Katalog", pocet: katalog.length },
    { id: "napady", label: "Návrhy od členek", pocet: napady.length },
    { id: "zpravy", label: "Automatické zprávy", pocet: zpravy.length },
  ];
  const titulKarty = zalozka === "katalog" ? `Katalog (${videt.length})`
    : zalozka === "napady" ? `Návrhy od členek (${napady.length})`
    : `Automatické zprávy (${zpravy.length})`;

  return (
    <Stranka>
      <GradientHeader
        title="Návyky klubu"
        napoveda="Katalog měsíčních výzev, které si členky můžou zapnout, návrhy, co posílají samy, a automatické zprávy, které jim k návykům chodí."
        icon={ListChecks}
        loading={loading}
        badge={data ? (
          <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
            {data.pocty.aktivni} z {data.pocty.celkem} se nabízí
          </span>
        ) : null}
        akce={[
          ...(zalozka === "katalog"
            ? [{
                klic: "pridat",
                ikona: Plus,
                popis: "Přidat návyk",
                onClick: () => setEdit({ navyk: prazdny(filtr === "vse" ? "jidlo" : filtr) }),
              }]
            : []),
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading },
        ]}
      />

      <Zalozky
        stitek="Část"
        aria-label="Část návyků"
        value={zalozka}
        onChange={setZalozka}
        items={zalozky.map((z) => ({ key: z.id, label: `${z.label} (${z.pocet})` }))}
      />

      {error && <ErrorNote onRetry={refetch}>Návyky se nepodařilo načíst: {String(error)}</ErrorNote>}

      <KartaPrehledu>
        <HlavickaDlazdice ikona={ListChecks} nazev={titulKarty} />
        <div className="p-3 sm:p-4">
        {loading && !data && <SkeletonRows rows={6} />}

        {zalozka === "katalog" && data && (
          <>
            <div className="mb-3 space-y-2">
              <RozbalovaciVyber
                stitek="Kategorie"
                aria-label="Filtr podle kategorie návyku"
                value={filtr}
                onChange={setFiltr}
                items={[
                  { key: "vse", label: `Vše (${katalog.length})` },
                  ...Object.entries(kategorie).map(([k, l]) => ({
                    key: k,
                    label: `${l} (${katalog.filter((x) => x.kategorie === k).length})`,
                  })),
                ]}
              />
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className="field pl-9" placeholder="Hledat v textech"
                  value={hledani} onChange={(e) => setHledani(e.target.value)} />
              </div>
            </div>

            {videt.length === 0 ? (
              <EmptyState icon={ListChecks} title="Žádný návyk neodpovídá filtru" hint="Zkus jinou kategorii nebo vymaž hledání." />
            ) : (
              <div className="overflow-x-auto -mx-4 sm:-mx-5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 text-left font-semibold sm:px-5">Návyk</th>
                      <th className="px-2 py-3 text-left font-semibold hidden md:table-cell">Kategorie</th>
                      <th className="px-2 py-3 text-left font-semibold hidden md:table-cell">Typ</th>
                      <th className="px-2 py-3 text-left font-semibold hidden lg:table-cell">Náročnost</th>
                      <th className="px-2 py-3 text-left font-semibold hidden lg:table-cell">Nastavení</th>
                      <th className="px-4 py-3 text-right font-semibold sm:px-5">Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videt.map((n) => (
                      <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                        <td className="px-4 py-3 sm:px-5">
                          <div className="flex items-start gap-2.5">
                            <span className="text-lg leading-none">{n.emoji || "•"}</span>
                            <div className="min-w-0">
                              <div className={"font-semibold " + (n.aktivni ? "text-slate-800" : "text-slate-400 line-through")}>
                                {n.text}
                              </div>
                              <div className="mt-0.5 text-slate-500">{n.proc}</div>
                              <div className="mt-1.5 flex flex-wrap gap-1 md:hidden">
                                <Pilulka>{kategorie[n.kategorie] || n.kategorie}</Pilulka>
                                <Pilulka>{n.typ === "tydenni" ? "týdenní" : "denní"}</Pilulka>
                                <Pilulka>náročnost {n.narocnost}</Pilulka>
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {n.skupina && <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">skupina {n.skupina}</span>}
                                {n.jenRucne && <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">automat nenabízí</span>}
                                {!n.aktivni && <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">nenabízí se</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 hidden md:table-cell text-slate-600">{kategorie[n.kategorie] || n.kategorie}</td>
                        <td className="px-2 py-3 hidden md:table-cell text-slate-600">{n.typ === "tydenni" ? "týdenní" : "denní"}</td>
                        <td className="px-2 py-3 hidden lg:table-cell text-slate-600">{n.narocnost}</td>
                        <td className="px-2 py-3 hidden lg:table-cell text-slate-600">
                          {n.parametr ? `${n.parametr.min} až ${n.parametr.max} ${n.parametr.jednotka}` : "—"}
                        </td>
                        <td className="px-4 py-3 sm:px-5">
                          <div className="flex justify-end gap-1.5">
                            <button onClick={() => setEdit({ navyk: { ...n } })} title="Upravit"
                              className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => prepnout(n)} title={n.aktivni ? "Přestat nabízet" : "Zase nabízet"}
                              className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50">
                              {n.aktivni ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {zalozka === "napady" && data && (
          <>
            <p className="mb-3 text-sm text-slate-500">
              Členka si vlastní návyk napsat nemůže, ale může navrhnout, co do nabídky přidat.
              Přijatý návrh se rovnou otevře jako nový návyk v katalogu.
            </p>
            {napady.length === 0 ? (
              <EmptyState icon={MessageSquare} title="Zatím žádné návrhy" hint="Objeví se tu, jakmile některá členka navrhne nový návyk." />
            ) : (
              <div className="overflow-x-auto -mx-4 sm:-mx-5">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3 text-left font-semibold sm:px-5">Návrh</th>
                      <th className="px-2 py-3 text-left font-semibold hidden md:table-cell">Od koho</th>
                      <th className="px-2 py-3 text-left font-semibold hidden lg:table-cell">Kdy</th>
                      <th className="px-4 py-3 text-right font-semibold sm:px-5">Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {napady.map((n) => (
                      <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                        <td className="px-4 py-3 sm:px-5">
                          <div className="flex items-start gap-2.5">
                            <Lightbulb className={"mt-0.5 h-4 w-4 flex-shrink-0 " + (n.stav === "novy" ? "text-amber-500" : "text-slate-300")} />
                            <div className="min-w-0">
                              <div className="text-slate-800">{n.text}</div>
                              <div className="mt-1 text-xs text-slate-500 md:hidden">{n.jmeno || n.email}, {n.kdy}</div>
                              {n.stav !== "novy" && (
                                <span className={`${znacka} mt-1`}>{n.stav === "prijato" ? "přijato" : "zamítnuto"}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 hidden md:table-cell text-slate-600">{n.jmeno || n.email}</td>
                        <td className="px-2 py-3 hidden lg:table-cell text-slate-500">{n.kdy}</td>
                        <td className="px-4 py-3 sm:px-5">
                          {n.stav === "novy" && (
                            <div className="flex justify-end gap-1.5">
                              <button
                                onClick={() => setEdit({ navyk: { ...prazdny("jidlo"), text: n.text.slice(0, 120) }, napad: n.id })}
                                className="btn btn-primary">
                                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Do katalogu</span>
                              </button>
                              <button onClick={() => napadStav(n.id, "zamitnuto")} title="Zamítnout"
                                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {zalozka === "zpravy" && data && (
          <>
            <p className="mb-3 text-sm text-slate-500">
              Klub se člence ozve sám ve chvílích, kdy s návykem lidé obvykle končí.
              Kdy se zpráva pošle, řeší kód; co v ní stojí, měníte tady.
            </p>
            <div className="overflow-x-auto -mx-4 sm:-mx-5">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 text-left font-semibold sm:px-5">Zpráva</th>
                    <th className="px-2 py-3 text-left font-semibold hidden lg:table-cell">Kdy se pošle</th>
                    <th className="px-4 py-3 text-right font-semibold sm:px-5">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {zpravy.map((z) => (
                    <tr key={z.klic} className="border-b border-slate-100 hover:bg-slate-50/70">
                      <td className="px-4 py-3 sm:px-5">
                        <div className="flex items-start gap-2.5">
                          <Bell className={"mt-0.5 h-4 w-4 flex-shrink-0 " + (z.aktivni ? "text-plum-500" : "text-slate-300")} />
                          <div className="min-w-0">
                            <div className={"font-semibold " + (z.aktivni ? "text-slate-800" : "text-slate-400 line-through")}>
                              {z.nadpis}
                            </div>
                            <div className="mt-0.5 text-slate-500">{z.text}</div>
                            <div className="mt-1 text-xs text-slate-400 lg:hidden">{z.kdy}</div>
                            {!z.aktivni && (
                              <span className="mt-1 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                                neposílá se
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 hidden lg:table-cell text-slate-500">{z.kdy}</td>
                      <td className="px-4 py-3 sm:px-5">
                        <div className="flex justify-end">
                          <button onClick={() => setEdit({ zprava: { ...z } })} title="Upravit"
                            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50">
                            <Pencil className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        </div>
      </KartaPrehledu>

      {edit?.navyk && (
        <Okno
          titul={edit.napad ? "Nový návyk z návrhu členky" : edit.navyk.id ? "Úprava návyku" : "Nový návyk"}
          ikona={edit.navyk.id ? Pencil : Plus}
          onZavrit={() => setEdit(null)}
        >
          <FormularNavyku navyk={edit.navyk} kategorie={kategorie} klice={klice} ukladam={ukladam}
            onUlozit={(n) => ulozitNavyk(edit.navyk.id ? { ...n, id: edit.navyk.id } : n, edit.napad)}
            onZrusit={() => setEdit(null)} />
        </Okno>
      )}

      {edit?.zprava && (
        <Okno titul={`Zpráva: ${edit.zprava.nazev}`} ikona={MessageSquare} onZavrit={() => setEdit(null)}>
          <FormularZpravy zprava={edit.zprava} ukladam={ukladam}
            onUlozit={ulozitZpravu} onZrusit={() => setEdit(null)} />
        </Okno>
      )}
    </Stranka>
  );
}
